import { useMutation, useQueryClient } from "@tanstack/react-query";
import { returnSubmit } from "../../../../enums/queryFunction/Submit";
import React from "react";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { useNavigate } from "react-router-dom";
import { DOC_CATEGORY } from "../../../../enums/QueryKeys";
import { HttpStatusCode } from "axios";

export const invalidate_queries = [DOC_CATEGORY];

export default function useSubmit(id, url, key, invalidateQueries = []) {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const updatedUrl = React.useRef(url);
  const setUpdatedUrl = (newUrl) => {
    updatedUrl.current = newUrl;
  };
  const lastError = React.useRef(null);
  const lastResponse = React.useRef(null);

  const queryClient = useQueryClient();

  const queryKey = id ? key + id : key;

  const submitCallback = async (obj, replaceData = false, navTo = null) => {
    setLoading(true);
    setSuccess(false);
    lastError.current = null;
    lastResponse.current = null;
    try {
      const validObj = typeof obj === "object" && obj !== null ? obj : {};
      const response = await mutateAsync(validObj);
      lastResponse.current = response;

      if (response?.status) {
        if (response.status == HttpStatusCode.Ok || response.status == 200) {
          return true;
        }
      }
      return false;
    } catch (error) {
      lastError.current = error;
      return false;
    }
  };

  function updateKey(key, response) {
  if (invalidate_queries.some((q) => String(key).startsWith(q))) {
    queryClient.invalidateQueries({ queryKey: [key] });
  } else {
    queryClient.setQueryData([key], (oldData) => {
      if (Array.isArray(oldData)) {
        return [...oldData, response?.data];
      }
      return [response?.data];
    });
  }
}

  const { mutateAsync } = useMutation({
    mutationFn: (data) => returnSubmit(updatedUrl.current, data),
    onSuccess: (response, { replaceData, navTo }) => {
      if (response?.data) {
        if (Array.isArray(queryKey)) {
          queryKey.forEach((key) => updateKey(key, response));
        } else {
          updateKey(key, response);
        }
      } else {
        // Fallback to invalidate queries if no data is returned, to refresh the query
        queryClient.invalidateQueries([queryKey]);
      }
      if (Array.isArray(invalidateQueries) && invalidateQueries.length > 0) {
        invalidateQueries.forEach((query) =>
          queryClient.invalidateQueries([query])
        );
      }
      handleSnackbar("success", "success");

      if (navTo) {
        navigate(navTo);
      }
      setSuccess(true);
      setLoading(false);
    },
    onError: (error) => {
      handleSnackbar(error, "error");
      setLoading(false);
      setSuccess(false);
    },
  });

  return {
    setUpdatedUrl,
    submitCallback,
    loading,
    success,
    getLastError: () => lastError.current,
    getLastResponse: () => lastResponse.current,
  };
}


export function useMessaging(id, url, key, invalidateQueries = []) {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const queryClient = useQueryClient();

  const queryKey = id ? key + id : key;

  const submitCallback = async (obj, replaceData = false, navTo = null) => {
    setLoading(true);
    setSuccess(false);
    try {
      const validObj = typeof obj === "object" && obj !== null ? obj : {};
      const response = await mutateAsync(validObj);

      if (response?.status) {
        if (response.status == HttpStatusCode.Ok || response.status == 200) {
          return response;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  function updateKey(key, response) {
  if (invalidate_queries.includes(String(key))) {
    queryClient.invalidateQueries([key]);
  } else {
    queryClient.setQueryData([key], (oldData) => {
      if (!oldData) return oldData;

      if (Array.isArray(oldData)) {
        return [...oldData, response?.data];
      }

      if (typeof oldData === "object") {
        const arrayKey = Object.keys(oldData).find(
          (k) => Array.isArray(oldData[k])
        );
        if (arrayKey) {
          return {
            ...oldData,
            [arrayKey]: [...(oldData[arrayKey] ?? []), response?.data],
          };
        }
      }

      return oldData;
    });
  }
}

  const { mutateAsync } = useMutation({
    mutationFn: (data) => returnSubmit(url, data),
    onSuccess: (response, { replaceData, navTo }) => {
      if (response?.data) {
        if (Array.isArray(queryKey)) {
          queryKey.forEach((key) => updateKey(key, response));
        } else {
          updateKey(key, response);
        }
      } else {
        // Fallback to invalidate queries if no data is returned, to refresh the query
        queryClient.invalidateQueries([queryKey]);
      }
      if (Array.isArray(invalidateQueries) && invalidateQueries.length > 0) {
        invalidateQueries.forEach((query) =>
          queryClient.invalidateQueries([query])
        );
      }
      handleSnackbar("success", "success");

      if (navTo) {
        navigate(navTo);
      }
      setSuccess(true);
      setLoading(false);
    },
    onError: (error) => {
      handleSnackbar(error, "error");
      setLoading(false);
      setSuccess(false);
    },
  });

  return {
    submitCallback,
    loading,
    success,
  };
}
